import { useEffect, useState } from 'react';
import { Card, EmptyState } from '../components/atoms';
import type { FormFieldConfig } from '../components/organisms';
import { FormBuilder } from '../components/organisms';
import { PageTemplate } from '../components/templates';
import { ApiError, useSettings, useUpdateSettings } from '../lib/api';

interface SettingsFormValues extends Record<string, string> {
  pollIntervalMinutes: string;
  requestTimeoutSeconds: string;
  retentionDays: string;
  remindEveryMinutes: string;
  timezone: string;
}

const FIELDS: FormFieldConfig<SettingsFormValues>[] = [
  { name: 'pollIntervalMinutes', label: 'Poll interval (minutes)', type: 'number', validation: { required: true, min: 1 } },
  { name: 'requestTimeoutSeconds', label: 'Request timeout (seconds)', type: 'number', validation: { required: true, min: 1 } },
  { name: 'retentionDays', label: 'Retention (days)', type: 'number', validation: { required: true, min: 1 } },
  { name: 'remindEveryMinutes', label: 'Remind every (minutes)', type: 'number', validation: { required: true, min: 0 } },
  { name: 'timezone', label: 'Timezone', validation: { required: true } },
];

export function SettingsPage() {
  const settingsQuery = useSettings();
  const updateSettings = useUpdateSettings();
  const [values, setValues] = useState<SettingsFormValues | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (settingsQuery.data) {
      setValues({
        pollIntervalMinutes: String(settingsQuery.data.pollIntervalMinutes),
        requestTimeoutSeconds: String(settingsQuery.data.requestTimeoutSeconds),
        retentionDays: String(settingsQuery.data.retentionDays),
        remindEveryMinutes: String(settingsQuery.data.remindEveryMinutes),
        timezone: settingsQuery.data.timezone,
      });
    }
  }, [settingsQuery.data]);

  if (settingsQuery.isPending || settingsQuery.error || !values) {
    return (
      <PageTemplate title="Settings">
        <Card raised>
          <EmptyState>
            {settingsQuery.error ? `Could not load settings: ${settingsQuery.error.message}` : 'Loading settings…'}
          </EmptyState>
        </Card>
      </PageTemplate>
    );
  }

  function update(name: keyof SettingsFormValues, value: string) {
    setValues((v) => (v ? { ...v, [name]: value } : v));
  }

  async function handleSubmit() {
    if (!values) return;
    setFormError(null);
    try {
      await updateSettings.mutateAsync({
        pollIntervalMinutes: Number(values.pollIntervalMinutes),
        requestTimeoutSeconds: Number(values.requestTimeoutSeconds),
        retentionDays: Number(values.retentionDays),
        remindEveryMinutes: Number(values.remindEveryMinutes),
        timezone: values.timezone,
      });
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : (error as Error).message || 'Could not save settings.');
    }
  }

  return (
    <PageTemplate title="Settings" meta="Global settings used by the poller and dashboard.">
      <Card className="max-w-lg p-6">
        <FormBuilder
          fields={FIELDS}
          values={values}
          onChange={update}
          onSubmit={handleSubmit}
          formError={formError}
          busy={updateSettings.isPending}
          submitLabel="Save"
        />
      </Card>
    </PageTemplate>
  );
}
